import React, { useEffect, useRef, useState } from 'react';
import { uploadB2BOrderDocuments } from '../../services/shipturtleApi';
import { showToast } from '../../utils/toast';
import './B2BOrders.css';

/**
 * Attach additional documents to a B2B order — packing lists, POs, challans,
 * signed PODs: whatever this particular order needs, in whatever number.
 *
 * One row per document, each with its own name. A row's file picker takes
 * several files at once: the first fills that row and the rest become rows of
 * their own, named after the file until someone types a better name. So one
 * document and twenty are the same gesture.
 *
 * The invoice and e-way bill are not uploaded here — they are fixed slots on
 * the order itself, managed from the Documents tab and the order form.
 */

// Mirrors ADDITIONAL_DOCUMENT_EXTENSIONS and DOCUMENT_MAX_BYTES on the server,
// so a bad file is caught before the upload rather than after it.
const ALLOWED_EXTENSIONS = [
  'pdf',
  'png',
  'jpg',
  'jpeg',
  'webp',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'csv',
];
const ACCEPT = ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(',');
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_PER_UPLOAD = 20;

const newRow = (file = null) => ({
  key: `d-${Math.random().toString(36).slice(2)}`,
  // A file arriving without a typed name is named after itself, minus the
  // extension — usually close to right, and always editable.
  name: file ? file.name.replace(/\.[^.]+$/, '') : '',
  file,
});

const extensionOf = (file) => (file?.name.split('.').pop() || '').toLowerCase();

const sizeLabel = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/** Why this row cannot be uploaded, or '' when it can. */
const rowProblem = (row) => {
  if (!row.file) return 'Choose a file.';
  if (!ALLOWED_EXTENSIONS.includes(extensionOf(row.file))) {
    return `${extensionOf(row.file).toUpperCase() || 'This'} files are not accepted.`;
  }
  if (row.file.size > MAX_BYTES) return `Over the 10 MB limit (${sizeLabel(row.file.size)}).`;
  if (!row.name.trim()) return 'Give it a name.';
  return '';
};

export default function B2BDocumentsModal({ order, onClose, onSaved }) {
  const [rows, setRows] = useState([newRow()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const pickers = useRef({});

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const updateRow = (key, patch) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const handleFiles = (row, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const [first, ...rest] = files;
    setRows((prev) => {
      const index = prev.findIndex((r) => r.key === row.key);
      const filled = {
        ...row,
        file: first,
        name: row.name.trim() ? row.name : newRow(first).name,
      };
      const next = [...prev];
      next.splice(index, 1, filled, ...rest.map((f) => newRow(f)));
      return next;
    });
  };

  const problems = rows.map(rowProblem);
  const tooMany = rows.length > MAX_PER_UPLOAD;
  const canSave = !saving && !tooMany && problems.every((p) => !p);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await uploadB2BOrderDocuments(
        order.id,
        rows.map((r) => ({ name: r.name.trim(), file: r.file }))
      );
      showToast({
        type: 'success',
        message: `${rows.length} document${rows.length === 1 ? '' : 's'} added to ${
          order.order_number
        }.`,
      });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.message || 'The upload failed. Try again.');
      // Some may have been stored before the failure, so refresh regardless.
      onSaved?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="b2bModalBackdrop"
      role="dialog"
      aria-modal="true"
      onClick={() => {
        if (!saving) onClose();
      }}
    >
      <div className="b2bModalPanel b2bModalNarrow" onClick={(e) => e.stopPropagation()}>
        <header className="b2bModalHead">
          <div>
            <h3>Add documents</h3>
            <span className="b2bModalSub">{order.order_number}</span>
          </div>
          <button
            type="button"
            className="b2bModalClose"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="b2bModalBody">
          <p className="b2bStepIntro">
            Name each document so it reads clearly in the Documents tab. Choose several files
            at once to add a row for each. PDF, image, Word, Excel or CSV — up to 10 MB each.
          </p>

          <div className="b2bDocRows">
            {rows.map((row, index) => (
              <div className="b2bDocRow" key={row.key}>
                <span className="b2bLineBadge">{index + 1}</span>

                <label className="field b2bDocName">
                  <span>
                    Document name <span className="b2bReq">*</span>
                  </span>
                  <input
                    value={row.name}
                    onChange={(e) => updateRow(row.key, { name: e.target.value })}
                    placeholder="e.g. Packing list"
                    maxLength={255}
                  />
                </label>

                <div className="field b2bDocFile">
                  <span>
                    File <span className="b2bReq">*</span>
                  </span>
                  <input
                    ref={(el) => {
                      pickers.current[row.key] = el;
                    }}
                    type="file"
                    multiple
                    accept={ACCEPT}
                    hidden
                    onChange={(e) => {
                      handleFiles(row, e.target.files);
                      // Cleared so choosing the same file again still fires.
                      e.target.value = '';
                    }}
                  />
                  <button
                    type="button"
                    className="b2bDocPick"
                    onClick={() => pickers.current[row.key]?.click()}
                    title={row.file ? row.file.name : undefined}
                  >
                    {row.file ? (
                      <>
                        <span className="b2bDocPickName">{row.file.name}</span>
                        <span className="b2bDocPickSize">{sizeLabel(row.file.size)}</span>
                      </>
                    ) : (
                      'Choose file(s)…'
                    )}
                  </button>
                </div>

                {rows.length > 1 && (
                  <button
                    type="button"
                    className="b2bLineRemove b2bDocRemove"
                    title="Remove this document"
                    aria-label={`Remove document ${index + 1}`}
                    onClick={() => setRows((prev) => prev.filter((r) => r.key !== row.key))}
                  >
                    ×
                  </button>
                )}

                {row.file && problems[index] && (
                  <small className="fieldError b2bDocProblem">{problems[index]}</small>
                )}
              </div>
            ))}
          </div>

          <button
            type="button"
            className="b2bAddLine"
            onClick={() => setRows((prev) => [...prev, newRow()])}
            disabled={rows.length >= MAX_PER_UPLOAD}
          >
            <span className="b2bAddLineIcon">+</span>
            Add another document
          </button>

          {tooMany && (
            <p className="fieldError">Upload at most {MAX_PER_UPLOAD} documents at a time.</p>
          )}
          {error && <p className="fieldError">{error}</p>}
        </div>

        <footer className="b2bModalFoot">
          <button type="button" className="btn btnGhost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btnPrimary"
            disabled={!canSave}
            onClick={handleSave}
          >
            {saving
              ? 'Uploading…'
              : `Upload ${rows.length} document${rows.length === 1 ? '' : 's'}`}
          </button>
        </footer>
      </div>
    </div>
  );
}
